import { FC, memo, useRef } from 'react';
import cn from 'classnames';
import type {
  SorterResult,
  FilterValue,
  TableCurrentDataSource,
} from 'antd/es/table/interface';
import { ColumnsType } from 'antd/lib/table';
import { TablePaginationConfig } from 'antd/es/table/interface';
import styles from './style.module.css';
import tableDefaults from '../../shared/antTableDefaults.module.css';
import { useTableHeight } from '../../../hooks/components/useTableHeight';
import { UsersRequest } from '../../../store/reducers/UsersSlice';
import { UsersTablet } from '../../../models/Users/Users';
import { TableFooter } from '../../TableFooter';
import { LoadingTable } from '../../shared/Loading';
import { TableResults } from '../../shared/TableResults';

interface Props {
  pageSize: number;
  totalCount: number;
  total: number;
  onChange: (page: number, pageItemCount: number) => void;
  callRequest: UsersRequest;
  allUsers: UsersTablet[];
  COLUMNS: ColumnsType<UsersTablet>;
  divider: { height: number; backgroundColor: string; margin: string };
  onTableChange?:
    | ((
        pagination: TablePaginationConfig,
        filters: Record<string, FilterValue | null>,
        sorter: SorterResult<UsersTablet> | SorterResult<UsersTablet>[],
        extra: TableCurrentDataSource<UsersTablet>,
      ) => void)
    | undefined;
  handlePageSizeChange: (value: string) => void;
  loading?: boolean;
  hasLoadError?: boolean;
}

export const UsersTable: FC<Props> = memo(
  ({
    pageSize,
    onChange,
    callRequest,
    total,
    allUsers,
    COLUMNS,
    divider,
    onTableChange,
    handlePageSizeChange,
    loading = false,
    hasLoadError = false,
  }: Props) => {
    const tableContainerRef = useRef<HTMLDivElement | null>(null);

    const { tableHeight, containerHeight, isTabletScroll } = useTableHeight({
      tableContainerRef,
      items: allUsers,
    });

    const tabletScroll = isTabletScroll ? { y: tableHeight } : undefined;

    return (
      <div ref={tableContainerRef} className={styles.Users}>
        <TableResults
          isEmpty={!hasLoadError && allUsers.length === 0 && total === 0}
          loading={loading}
        >
          <div style={{ height: `${containerHeight}px` }}>
            <LoadingTable<UsersTablet>
              columns={COLUMNS}
              style={{ tableLayout: 'auto' }}
              className={cn(
                styles.table,
                styles.stickyHeader,
                tableDefaults.defaultTable,
              )}
              rowClassName={styles.customRow}
              onChange={onTableChange}
              pagination={false}
              showSorterTooltip={false}
              dataSource={allUsers.map((call, index) => ({
                ...call,
                index,
              }))}
              rowKey={(record) => record.id}
              scroll={tabletScroll}
              loading={loading}
            />
          </div>
          <div className={styles.divider} style={divider} />

          <TableFooter
            pageIndex={callRequest.PageIndex}
            pageSize={pageSize}
            total={total}
            onPageChange={onChange}
            onPageSizeChange={handlePageSizeChange}
            disabled={loading}
          />
        </TableResults>
      </div>
    );
  },
);

UsersTable.displayName = 'UsersTable';
